import { type ReactNode, useId, useState } from 'react';
import { enableModalConfirmationAutofocus } from '@gonfalon/dogfood-flags/dogfood-flags';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Alert, Button, Dialog, Heading, IconButton, Modal, ModalOverlay, ProgressBar } from '@launchpad-ui/components';

import { trackReviewPayloadChangeCancelled, trackReviewPayloadChangeConfirmed } from '../../../analytics';

import styles from './PayloadChangeDialogBase.module.css';

export type PayloadChangeDialogBaseProps = {
  /** Controls modal visibility. Parent owns the state so the originating UI stays mounted underneath. */
  isOpen: boolean;
  /** Called when the user dismisses via Cancel, the close button, or the overlay. */
  onCancel: () => void;
  /**
   * Fires the underlying mutation. The shell swallows rejections internally so the
   * dialog stays mounted; the caller is expected to surface the error via snackbar
   * or inline alert and the user can retry without losing in-flight selection state.
   */
  onConfirm: () => Promise<void>;
  /** External submitting flag — typically wired from the parent mutation's `isPending`. */
  isSubmitting: boolean;
  /** Heading rendered above the diff body — e.g. "Payload changes" or "Affected payloads". */
  sectionHeading: string;
  /** Diff content (rows, env-grouped tables, etc.) provided by the consumer. */
  children: ReactNode;
  /**
   * Optional alert content rendered inside the warning Alert wrapper. When
   * omitted, falls back to the standard "This change takes effect immediately"
   * warning used by the SDK-key edit and view-resource flows. Consumers with
   * direction-aware or interpolated alert copy (e.g. the mobile-SDK toggle
   * confirm) pass their own content.
   */
  alert?: ReactNode;
  /**
   * Optional override for the primary action button label. Defaults to
   * "Update" — the verbatim copy used by edit/remove flows. The flag-creation
   * flow passes `"Continue"` because the dialog precedes the
   * actual `POST /flags` rather than confirming an in-progress edit.
   */
  confirmLabel?: string;
};

const defaultAlert = (
  <>
    <h3 className={styles.alertHeading}>
      <Message id="sdkKeys.payloadChangeDialog.warningHeading" />
    </h3>
    <p className={styles.alertBody}>
      <Message id="sdkKeys.payloadChangeDialog.warningBody" />
    </p>
  </>
);

/**
 * Shared confirmation-dialog shell for any change that may impact SDK key
 * payloads. Centralises the modal scaffold, the verbatim warning alert ("This
 * change takes effect immediately" + standardised body), the "Review before
 * updating" title, the Cancel/Update footer, and the local-confirm guard
 * pattern shared by the consumers in `static/ld/components/` (the SDK key edit
 * confirm and the view resource change confirm).
 *
 * The shell is presentational: the parent owns `isOpen`, the mutation, and the
 * `isSubmitting` flag from whatever loading state the mutation exposes.
 */
export function PayloadChangeDialogBase({
  isOpen,
  onCancel,
  onConfirm,
  isSubmitting,
  sectionHeading,
  children,
  alert = defaultAlert,
  confirmLabel = msg('sdkKeys.payloadChangeDialog.confirm'),
}: PayloadChangeDialogBaseProps) {
  // Guards against overlapping confirms if the parent forgets to flip `isSubmitting`.
  const [isLocallyConfirming, setIsLocallyConfirming] = useState(false);
  const isBusy = isSubmitting || isLocallyConfirming;
  const titleId = useId();

  const handleOpenChange = (nextIsOpen: boolean) => {
    if (!nextIsOpen && !isBusy) {
      trackReviewPayloadChangeCancelled();
      onCancel();
    }
  };

  const handleConfirm = async () => {
    if (isBusy) {
      return;
    }
    setIsLocallyConfirming(true);
    await runWithCleanup(
      async () => {
        try {
          await onConfirm();
          trackReviewPayloadChangeConfirmed();
        } catch {
          // Parent surfaces mutation errors; swallow here so the
          // dialog stays mounted and the user can retry without losing state.
        }
      },
      () => {
        setIsLocallyConfirming(false);
      },
    );
  };

  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={handleOpenChange}>
      <Modal className={styles.modal}>
        <Dialog aria-labelledby={titleId}>
          <>
            <div slot="header">
              <Heading slot="title" id={titleId}>
                <Message id="sdkKeys.payloadChangeDialog.heading" />
              </Heading>
              <IconButton
                icon="cancel"
                variant="minimal"
                onPress={() => handleOpenChange(false)}
                aria-label={msg('sdkKeys.payloadChangeDialog.closeLabel')}
                isDisabled={isBusy}
              />
            </div>
            <div slot="body">
              <Alert status="warning" className={styles.alert}>
                {alert}
              </Alert>
              <h3 className={styles.sectionHeading}>{sectionHeading}</h3>
              {children}
            </div>
            <div slot="footer">
              <Button onPress={() => handleOpenChange(false)} isDisabled={isBusy}>
                {msg('sdkKeys.payloadChangeDialog.cancel')}
              </Button>
              <Button
                variant="primary"
                onPress={() => {
                  void handleConfirm();
                }}
                isDisabled={isBusy}
                autoFocus={enableModalConfirmationAutofocus() && !isBusy}
              >
                {isBusy ? (
                  <ProgressBar
                    aria-label={msg('sdkKeys.payloadChangeDialog.updatingLabel')}
                    isIndeterminate
                    size="small"
                  />
                ) : (
                  confirmLabel
                )}
              </Button>
            </div>
          </>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}

function runWithCleanup<T>(run: () => Promise<T>, cleanup: () => void): Promise<T> {
  return run().then(
    (value) => {
      cleanup();
      return value;
    },
    (error) => {
      cleanup();
      throw error;
    },
  );
}
